import { useContext, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { AudioVisualizer } from 'react-audio-visualize'

import { stripHTML } from '@aytm/lib/common/func'
import { highlightSearchString } from '@aytm/app/stats/page/comments/lib/utils'

import useIsVisible from '@aytm/lib/hooks/useIsVisible'

import { OEContext } from '@aytm/app/stats/page/comments'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import { Dropdown } from 'primereact/dropdown'

import style from '@aytm/app/stats/page/comments/comments/styles/OEItem.module.scss'

const synth = window.speechSynthesis

const getVoiceList = () => {
  if (0 < window.voicesListSelect?.length) return
  if (!synth) return
  if (0 < synth.getVoices().length) {
    window.voicesListSelect = synth.getVoices().map((voice, i) => {
      return { text: `${voice.name} (${voice.lang})`, value: i }
    })
  } else setTimeout(getVoiceList, 500)
}
getVoiceList()

const AudioResponse = ({ node, voiceBusy, setVoiceBusy }) => {
  const { searchState } = useContext(OEContext)

  const { request, isHighlightSearchTerms } = searchState

  const array = node.t

  const [blobs, setBlobs] = useState({})

  const [time, setTime] = useState(0)
  const [played, setPlayed] = useState('')
  const [playedAll, setPlayedAll] = useState(null)

  const [llmVoice, setLlmVoice] = useState(Number(window.localStorage.getItem('aytmLlmVoice') || 0))
  const [userVoice, setUserVoice] = useState(Number(window.localStorage.getItem('aytmUserVoice') || 0))

  const initRef = useRef(false)

  // One <audio> element per audio_uid; a single shared ref pointed at only the
  // last-mounted element (see _open-questions.md §OQ-23).
  const audioRefs = useRef({})
  const blockRef = useRef(null)

  const currentPlayed = useRef(null)
  const utterThis = useRef(null)

  // threshold 0, not the hook's default: this ref holds the whole transcript, which is far
  // taller than the viewport, so its intersection ratio can never reach 10% and the observer
  // would never report it visible
  const isVisible = useIsVisible(blockRef, 0)

  const updateLlmVoice = event => {
    setLlmVoice(Number(event.value || 0))
    window.localStorage.setItem('aytmLlmVoice', Number(event.value || 0))
  }

  const updateUserVoice = event => {
    setUserVoice(Number(event.value || 0))
    window.localStorage.setItem('aytmUserVoice', Number(event.value || 0))
  }

  const playAudio = uid => {
    if (!uid) return
    const el = audioRefs.current[uid]
    if (!el) return
    if (played == uid) {
      setPlayed(null)
      el.pause()
      return
    }
    const reader = new FileReader()
    setPlayed(uid)
    reader.onload = event => {
      el.src = event.target.result
      void el.play()
    }
    reader.readAsDataURL(blobs[uid])
    el.load()
    el.onload = () => {
      void el.play()
    }
  }

  const onTimeUpdate = uid => setTime(audioRefs.current[uid]?.currentTime ?? 0)

  const nextTrack = () => {
    setTimeout(() => {
      if (null !== playedAll) {
        setPlayedAll(playedAll < node.t.length - 1 ? playedAll + 1 : null)
      }
      if (playedAll == node.t.length - 1) setVoiceBusy(false)
    }, 150)
  }

  const audioEnded = () => {
    setPlayed(null)
    nextTrack()
  }

  const playAll = () => {
    if (!synth) return
    setPlayedAll(null !== playedAll ? null : 0)
    if (null !== playedAll && utterThis.current) {
      synth.cancel()
      utterThis.current = null
      setPlayedAll(null)
      currentPlayed.current = null
      if (played && audioRefs.current[played]) {
        audioRefs.current[played].pause()
      }
      setPlayed(null)
      setVoiceBusy(false)
    }
  }

  const popupContent = useCallback(
    () => (
      <div className={style.popupContent}>
        <span className={style.spanPopup}>Skipper:</span>
        <Dropdown
          className={style.selectPopup}
          ariaLabel="Skipper voice"
          aria-label="Skipper voice"
          value={llmVoice}
          options={window.voicesListSelect}
          optionValue="value"
          optionLabel="text"
          fixedDropdownDir="bottom"
          disabled={!window.voicesListSelect?.length}
          onChange={updateLlmVoice}
        />
        <span className={style.spanPopup}>Respondent:</span>
        <Dropdown
          className={style.selectPopup}
          ariaLabel="Respondent voice"
          aria-label="Respondent voice"
          value={userVoice}
          options={window.voicesListSelect}
          optionValue="value"
          optionLabel="text"
          fixedDropdownDir="bottom"
          disabled={!window.voicesListSelect?.length}
          onChange={updateUserVoice}
        />
      </div>
    ),
    [llmVoice, userVoice]
  )

  // Fetch the audio blobs once the block scrolls into view. Runs in an effect
  // (not during render) with an AbortController + functional setState, so it
  // never sets state after unmount or drops a concurrent resolution (§OQ-24).
  useEffect(() => {
    if (initRef.current || !isVisible) return
    initRef.current = true
    const controller = new AbortController()
    node.t
      .filter(block => block.audio_uid)
      .forEach(block =>
        fetch(block.audio_url, { signal: controller.signal })
          .then(r => r.blob())
          .then(r => setBlobs(prev => ({ ...prev, [block.audio_uid]: r })))
          .catch(error => {
            //eslint-disable-next-line no-console
            if ('AbortError' != error.name) console.log(error)
          })
      )
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  // Drive the "play all" synthesis from an effect rather than during render —
  // synth.speak / setVoiceBusy are side effects and double-fire under StrictMode
  // when run in the render body (§OQ-25).
  useEffect(() => {
    if (null === playedAll || playedAll == currentPlayed.current || !synth) return
    currentPlayed.current = playedAll
    setVoiceBusy(true)
    if (node.t[playedAll].audio_uid) {
      playAudio(node.t[playedAll].audio_uid)
    } else {
      utterThis.current = new SpeechSynthesisUtterance(stripHTML(node.t[playedAll].text))
      utterThis.current.voice = synth.getVoices()['llm' == node.t[playedAll].type ? llmVoice : userVoice]
      utterThis.current.onend = () => {
        nextTrack()
      }
      synth.speak(utterThis.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playedAll])

  const renderAudioVisualizer = block =>
    block.audio_uid ? (
      blobs[block.audio_uid] ? (
        <>
          <AudioVisualizer
            blob={blobs[block.audio_uid]}
            width={100}
            height={25}
            currentTime={played == block.audio_uid ? time : null}
            barPlayedColor={'#73b0ac'}
          />
          <audio
            ref={el => {
              if (el) audioRefs.current[block.audio_uid] = el
              else delete audioRefs.current[block.audio_uid]
            }}
            onTimeUpdate={() => onTimeUpdate(block.audio_uid)}
            onEnded={audioEnded}
          />
          {!voiceBusy ? (
            <i
              className={`${style.icon} ${style.blue} ${
                played == block.audio_uid ? 'icon-aytmfd-stop' : 'icon-aytmfd-video-play'
              }`}
              onClick={() => playAudio(block.audio_uid)}
            />
          ) : null}
          <a href={block.audio_url} download target="blank">
            Download audio
          </a>
        </>
      ) : (
        <i className={`${style['spinner']} icon-aytmfd-refresh-v2 aytmfd-spin`} />
      )
    ) : null

  const renderSkipperBlock = (block, index) => {
    let content = block.text
    if (isHighlightSearchTerms) {
      content = highlightSearchString(block.text, request.query)
    }

    return (
      <div
        key={index + 1}
        className={classNames(style['comment-answer-block-container'], style['comment-answer-block-skipper'])}
      >
        <div className={style['comment-answer-block-header']}>
          <i className="icon-aytmfd-qconversation-skipper" />
          <span>Skipper</span>
          {renderAudioVisualizer(block)}
        </div>
        <div className={style['comment-answer-block-text']} dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    )
  }

  const renderRespondentBlock = (block, index) => {
    let content = block.text
    if (isHighlightSearchTerms) {
      content = highlightSearchString(block.text, request.query)
    }

    return (
      <div
        key={index + 1}
        className={classNames(style['comment-answer-block-container'], style['comment-answer-block-respondent'])}
      >
        <div className={style['comment-answer-block-header']}>
          <i className="icon-aytmfd-qconversation-user" />
          <span>Respondent</span>
          {renderAudioVisualizer(block)}
        </div>
        <div className={style['comment-answer-block-text']} dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    )
  }

  const renderSvg = () => (
    <svg width="10px" height="8px" viewBox="0 0 10 8" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <g
        id="Audio"
        transform="translate(0.000000, 0.500000)"
        stroke="currentColor"
        stroke-width="1"
        fill-rule="evenodd"
        stroke-linecap="round"
      >
        <line x1="8.5" y1="0.493135" x2="8.5" y2="6.50687" id="Line-5">
          <animate
            attributeType="XML"
            attributeName="y1"
            values="2;0;2"
            keyTimes="0;0.5;1"
            dur=".8s"
            repeatCount="indefinite"
          ></animate>
          <animate
            attributeType="XML"
            attributeName="y2"
            values="5;7;5"
            keyTimes="0;0.5;1"
            dur=".8s"
            repeatCount="indefinite"
          ></animate>
        </line>
        <line x1="6.5" y1="0.789016" x2="6.5" y2="6.21098" id="Line-4">
          <animate
            attributeType="XML"
            attributeName="y1"
            values="0;2;0"
            keyTimes="0;0.5;1"
            dur=".5s"
            repeatCount="indefinite"
          ></animate>
          <animate
            attributeType="XML"
            attributeName="y2"
            values="7;5;7"
            keyTimes="0;0.5;1"
            dur=".5s"
            repeatCount="indefinite"
          ></animate>
        </line>
        <line x1="4.5" y1="1.67582" x2="4.5" y2="5.32418" id="Line-3">
          <animate
            attributeType="XML"
            attributeName="y1"
            values="1;3;1"
            keyTimes="0;0.5;1"
            dur=".6s"
            repeatCount="indefinite"
          ></animate>
          <animate
            attributeType="XML"
            attributeName="y2"
            values="6;4;6"
            keyTimes="0;0.5;1"
            dur=".6s"
            repeatCount="indefinite"
          ></animate>
        </line>
        <line x1="2.5" y1="1.14678" x2="2.5" y2="5.85322" id="Line-2">
          <animate
            attributeType="XML"
            attributeName="y1"
            values="2;1;2"
            keyTimes="0;0.5;1"
            dur=".7s"
            repeatCount="indefinite"
          ></animate>
          <animate
            attributeType="XML"
            attributeName="y2"
            values="5;6;5"
            keyTimes="0;0.5;1"
            dur=".7s"
            repeatCount="indefinite"
          ></animate>
        </line>
        <line x1="0.5" y1="1.67582" x2="0.5" y2="5.32418" id="Line-1">
          <animate
            attributeType="XML"
            attributeName="y1"
            values="3;0;3"
            keyTimes="0;0.5;1"
            dur=".9s"
            repeatCount="indefinite"
          ></animate>
          <animate
            attributeType="XML"
            attributeName="y2"
            values="4;7;4"
            keyTimes="0;0.5;1"
            dur=".9s"
            repeatCount="indefinite"
          ></animate>
        </line>
      </g>
    </svg>
  )

  return (
    <div className={style['comment-answer-block']} ref={blockRef}>
      {null !== playedAll || !voiceBusy ? (
        <>
          <div className={style.iconAll}>
            <i
              className={null !== playedAll ? 'icon-aytmfd-stop ' + style.stop : 'icon-aytmfd-qconversation-playback'}
              title={`Respondents replied to Skipper's written prompts and were not offered voice-over, but you can listen to the synthesized conversation`}
              onClick={() => playAll()}
            />
            {null !== playedAll ? renderSvg() : null}
          </div>
          <Popup width="240px" trigger="click" direction="righttop" popupOffset={19} content={popupContent}>
            <i className={`${style.iconAllConfig} icon-aytmfd-config`} />
          </Popup>
        </>
      ) : null}

      {array.map((block, index) =>
        'llm' == block.type ? renderSkipperBlock(block, index) : renderRespondentBlock(block, index)
      )}
    </div>
  )
}

AudioResponse.propTypes = {
  node: PropTypes.object,
  nodeOptions: PropTypes.object,
  toolBoxOptions: PropTypes.array,
  path: PropTypes.string,
  tags: PropTypes.array,
  responseId: PropTypes.number,
  dataId: PropTypes.string,
  survey: PropTypes.object,
  answer: PropTypes.object,
  question: PropTypes.object,
  sentiment: PropTypes.string,
  best_span_str: PropTypes.string,
  isVirtualOrXpert: PropTypes.bool,
  isVideoOrImage: PropTypes.bool,
  hasTitle: PropTypes.bool,
  isRejected: PropTypes.bool,
  onUpdateSchema: PropTypes.func,
  voiceBusy: PropTypes.bool,
  setVoiceBusy: PropTypes.func
}

AudioResponse.displayName = 'AudioResponse'

export default AudioResponse
